import type { TFunction } from 'i18next';
import type { OperationalExceptionOptions } from '../operationalExceptions/operationalExceptionFlow';
import type { OperationalWarning } from '../operationalExceptions/operationalExceptionText';
import { normalizeUnitCode } from '../../utils/units';

/**
 * „Zdejmij nadmiar z tego auta” (production ZD 10/43, 2026-10-28): a save the
 * server answers with CARGO_OVER_ALLOCATION can be resent with every overflowing
 * ZK line cut to what the other trucks leave of it (ordered − elsewhere), a line
 * with nothing left taken off this truck. The operator then needs no reason for
 * a load that is not real (the production one was „gggggggggg”).
 *
 * Pure; the server re-expresses each quantity into its pcs/m²/m³ set and checks
 * the corrected save again. Returns null (no correction offered) when it cannot
 * be done without a product decision:
 * - the save carries no `cargoItems` (the overflow is in rows it does not send),
 * - the truck is saved as delivered (attempted = delivered quantity),
 * - a cargo row is in another unit than its order line,
 * - a cut or removed row carries an entered goods value or manual freight
 *   (money is operator input, never re-split here),
 * - the save also sends the ZD goods of this truck (`supplierCargoItems`; on a
 *   factory → customer truck derived from the ZK rows, paired ones and those
 *   matched by variant): trimming only the ZK side would leave the ZD side at
 *   the old sum (review PR #445),
 * - a ZK would be left without any cargo (taking a ZK off is an unlink).
 */
export function correctCargoOverAllocation(
  warnings: OperationalWarning[],
  payload: Record<string, unknown>,
): Record<string, unknown> | null {
  const overflow = warnings
    .filter((warning) => warning.code === 'CARGO_OVER_ALLOCATION')
    .flatMap((warning) => (Array.isArray(warning.details?.overflow) ? warning.details!.overflow : [])) as Array<Record<string, unknown>>;
  if (overflow.length === 0) return null;
  if (!Array.isArray(payload.cargoItems)) return null;
  if (String(payload.status || '').toUpperCase() === 'DELIVERED') return null;

  const limits = new Map<string, { available: number; unit: string | null }>();
  overflow.forEach((row) => {
    const ordered = Number(row.ordered);
    const elsewhere = Number(row.allocatedElsewhere);
    if (!row.orderProductId || !Number.isFinite(ordered) || !Number.isFinite(elsewhere)) return;
    limits.set(String(row.orderProductId), {
      available: Math.max(Math.round((ordered - elsewhere) * 1000) / 1000, 0),
      unit: normalizeUnitCode(String(row.unit || 'pcs')),
    });
  });
  if (limits.size !== overflow.length) return null;

  const cargoItems = payload.cargoItems as Array<Record<string, unknown>>;
  const next: Array<Record<string, unknown>> = [];
  for (const item of cargoItems) {
    const limit = limits.get(String(item.orderProductId || ''));
    const quantity = Number(item.quantity);
    if (!limit || !(quantity > limit.available)) {
      next.push(item);
      continue;
    }
    if (normalizeUnitCode(String(item.unit || 'pcs')) !== limit.unit) return null;
    if (Number(item.goodsValueTotal || 0) !== 0 || (item.manualAllocatedTransportCost ?? null) !== null) return null;
    if (Array.isArray(payload.supplierCargoItems)) return null;
    // A line with nothing left is not on this truck at all: its row goes.
    if (limit.available <= 0) continue;
    const { quantityPcs: _pcs, quantityM2: _m2, quantityM3: _m3, ...rest } = item;
    next.push({ ...rest, quantity: limit.available });
  }
  if (next.length === cargoItems.length && next.every((item, index) => item === cargoItems[index])) return null;

  const orderIdsBefore = new Set(cargoItems.map((item) => String(item.orderId || '')));
  const orderIdsAfter = new Set(next.map((item) => String(item.orderId || '')));
  if (Array.from(orderIdsBefore).some((orderId) => !orderIdsAfter.has(orderId))) return null;

  return { ...payload, cargoItems: next };
}

/**
 * The `correction` option of `submitWithOperationalException` for a truck save
 * (create and edit). `onApplied` runs when the operator chose it, so the page
 * can say what the saved truck carries.
 */
export function cargoOverflowCorrectionOption(
  t: TFunction,
  onApplied: () => void,
): NonNullable<OperationalExceptionOptions['correction']> {
  return {
    label: t('transport:overflowCorrection.label'),
    note: t('transport:overflowCorrection.note'),
    apply: correctCargoOverAllocation,
    onApplied,
  };
}
